import React, { FC } from 'react'

import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'

import Container from 'components/layouts/atoms/Container'
import SVGIcon from 'components/common/atoms/SVGIcon'
import UpSVG from 'svgs/up.svg'
import { useRouter } from 'next/router'

interface PathProps {
  name: string
  url: string
  idx?: string
}

type BreadcrumbProps = {
  paths?: PathProps[]
  hideOnMobile?: boolean
  background?: string
}

const breadcrumbStyle = (theme: ThemeType, hideOnMobile: boolean, background?: string) => css`
  background-color: ${background || theme.neutral.white};
  .breadcrumb {
    &__wrapper {
      padding: 16px 0;
      display: inline-flex;
      flex-wrap: wrap;
      list-style: none;
      align-items: center;
      li {
        display: flex;
        align-items: center;
      }
      li:last-of-type {
        .breadcrumb__text {
          color: ${theme.gray.gray70};
          cursor: auto;
        }
        .breadcrumb__arrow {
          display: none;
        }
      }
      li:not(:last-of-type) {
        .breadcrumb__link:hover {
          .breadcrumb__text {
            text-decoration: underline;
          }
        }
      }
    }
    &__link {
      display: flex;
    }
    &__text {
      color: ${theme.campus.aqua30};
      font-size: 12px;
      line-height: 1.5;
    }
    &__arrow {
      transform: rotate(90deg);
      padding: 0 8px;
    }
  }

  @media screen and (max-width: 767px) {
    display: ${hideOnMobile ? 'none' : 'block'};
  }
`

const BreadcrumbItem: FC<PathProps> = ({ name, url, idx }) => {
  const theme: ThemeType = useTheme()
  const router = useRouter()

  return (
    <li itemProp="itemListElement" itemScope itemType="http://schema.org/ListItem">
      <a
        href={`https://campus.quipper.com${url === '#' ? router.asPath : url}`}
        itemProp="item"
        className="breadcrumb__link"
        onClick={(e) => url === '#' && e.preventDefault()}
      >
        <span itemProp="name" className="breadcrumb__text">
          {name}
        </span>
      </a>
      <span className="breadcrumb__arrow">
        <SVGIcon name="up" size="xs" color={theme.gray.gray50}>
          <UpSVG />
        </SVGIcon>
      </span>
      <meta itemProp="position" content={idx} />
    </li>
  )
}

const Breadcrumb: FC<BreadcrumbProps> = ({ background, paths, hideOnMobile = false }) => {
  const theme: ThemeType = useTheme()
  return (
    <div className="breadcrumb" css={breadcrumbStyle(theme, hideOnMobile, background)}>
      <Container>
        <ol className="breadcrumb__wrapper" itemScope itemType="http://schema.org/BreadcrumbList">
          <BreadcrumbItem name="Home" url="" idx="1" />
          {paths?.map(({ name, url }, idx) => {
            return <BreadcrumbItem name={name} url={url} idx={`${idx + 2}`} key={`brcc-${idx}`} />
          })}
        </ol>
      </Container>
    </div>
  )
}

export default Breadcrumb
